import ClosingNotice from './ClosingNotice';
import React, { useState } from 'react';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';

function LandingPage({ onJoinWaitlist, onStartTracking, onBlog, onGetTheApp }) {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const chapters = [
    {
      num: '01',
      title: 'Something feels wrong',
      body: 'The changes crept in quietly. You start logging what you notice — the sleepless nights, the brain fog, the moods that come from nowhere. Day by day, you build your own record.',
      tag: 'Pre-diagnosis',
    },
    {
      num: '02',
      title: 'Getting answers',
      body: 'The appointment is booked. Instead of stumbling through symptoms from memory, you walk in with a clear, structured picture of the past month. You get taken seriously.',
      tag: 'The appointment',
    },
    {
      num: '03',
      title: 'Life on HRT',
      body: 'You\'re on HRT. You keep tracking through the change, building a clear week-by-week record of how you feel. Your next review starts from what actually happened, not from memory.',
      tag: 'On HRT',
    },
    {
      num: '04',
      title: 'Finding my normal',
      body: 'A new, stable baseline. You know your body. You have the data. You walk into every appointment in control of your own story.',
      tag: 'Your new normal',
    },
  ];

  return (
    <div style={styles.container}>
      <style>{`

        * { box-sizing: border-box; }

        @media (max-width: 640px) {
          .hero-headline { font-size: 42px !important; }
          .chapters-grid { flex-direction: column !important; }
          .stats-row { flex-direction: column !important; gap: 24px !important; }
          .inner { padding: 0 20px !important; }
          .hero-section { padding: 72px 0 64px !important; }
          .section-pad { padding: 64px 0 !important; }
        }
      `}</style>

      <div className="inner" style={styles.inner}>

        <ClosingNotice />

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logo}>
            <span style={styles.logoAppt}>Appt</span>
            <span style={styles.logoNote}>Note</span>
          </div>
          <div style={styles.navLinks}>
            <button style={styles.navLink} onClick={onBlog}>Journal</button>
            <button style={styles.navCta} onClick={onStartTracking}>Start free</button>
          </div>
        </div>

        {/* Hero */}
        <div className="hero-section" style={styles.hero}>
          <div style={styles.heroPill}>Women's health · Your story</div>
          <h1 className="hero-headline" style={styles.headline}>
            This is your story.<br />
            <em style={styles.headlineEm}>Start writing it.</em>
          </h1>
          <p style={styles.subheadline}>
            Every symptom logged. Every pattern spotted. Every appointment prepared.<br />
            From perimenopause to your new normal — ApptNote tracks every chapter.
          </p>
          <div style={styles.heroActions}>
            <button style={styles.ctaButton} onClick={onStartTracking}>
              Start your story free
            </button>
            <p style={styles.noSignup}>Free to track · No credit card needed</p>
          </div>
        </div>

        {/* Stats strip */}
        <div className="stats-row" style={styles.statsRow}>
          {[
            { stat: '70+', label: 'Days tracked by our longest user' },
            { stat: '28+', label: 'Symptoms tracked across every chapter' },
            { stat: '3 min', label: 'Daily check-in, no more' },
          ].map(({ stat, label }) => (
            <div key={stat} style={styles.statItem}>
              <div style={styles.statNumber}>{stat}</div>
              <div style={styles.statLabel}>{label}</div>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Chapters section */}
        <div className="section-pad" style={styles.section}>
          <div style={styles.sectionLabel}>YOUR JOURNEY</div>
          <h2 style={styles.sectionHeading}>
            Every chapter deserves to be written
          </h2>
          <p style={styles.sectionSub}>
            Perimenopause isn't a single moment. It's a story — with distinct chapters,
            each one asking something different of you.
          </p>
          <div className="chapters-grid" style={styles.chaptersGrid}>
            {chapters.map((ch) => (
              <div key={ch.num} style={styles.chapterCard}>
                <div style={styles.chapterTag}>{ch.tag}</div>
                <div style={styles.chapterNum}>{ch.num}</div>
                <h3 style={styles.chapterTitle}>{ch.title}</h3>
                <p style={styles.chapterBody}>{ch.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* The report feature */}
        <div className="section-pad" style={styles.section}>
          <div style={styles.sectionLabel}>THE APPOINTMENT</div>
          <div style={styles.reportFeature}>
            <div style={styles.reportText}>
              <h2 style={styles.reportHeading}>
                Walk in prepared.<br />
                <em style={{ fontStyle: 'italic' }}>Every time.</em>
              </h2>
              <p style={styles.reportBody}>
                ApptNote Pro generates a doctor-ready report from your tracking data —
                written in your voice, not clinical language. Your symptoms. Your patterns.
                Your four things you want to talk about.
              </p>
              <p style={styles.reportBody}>
                Hand it to your doctor before you sit down. No more forgetting. No more
                being dismissed in 8 minutes.
              </p>
              <button style={styles.ctaButtonOutline} onClick={onStartTracking}>
                Start tracking to unlock your report
              </button>
            </div>
            <div style={styles.reportVisual}>
              <div style={styles.reportCard}>
                <div style={styles.reportCardHeader}>
                  <span style={styles.reportCardLogo}>
                    <span style={{ color: '#2C2C2A' }}>Appt</span>
                    <span style={{ color: '#D4A5A5' }}>Note</span>
                  </span>
                  <span style={styles.reportCardDate}>My appointment report</span>
                </div>
                <div style={styles.reportCardSection}>
                  <div style={styles.reportCardLabel}>In my own words</div>
                  <div style={styles.reportCardText}>
                    "The past month has been difficult. My sleep has been broken most nights and my mood has been very low — lower than I've felt in years…"
                  </div>
                </div>
                <div style={styles.reportCardSection}>
                  <div style={styles.reportCardLabel}>What I want to discuss</div>
                  {['HRT dosage review', 'Sleep quality', 'Mood patterns', 'Next steps'].map((item, i) => (
                    <div key={i} style={styles.reportCardPoint}>
                      <span style={styles.reportCardDot}>{i + 1}</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
                <div style={styles.reportCardGreeneBars}>
                  {[
                    { label: 'Vasomotor', pct: 78 },
                    { label: 'Psychological', pct: 65 },
                    { label: 'Sleep', pct: 82 },
                  ].map(({ label, pct }) => (
                    <div key={label} style={styles.reportBarRow}>
                      <span style={styles.reportBarLabel}>{label}</span>
                      <div style={styles.reportBarTrack}>
                        <div style={{ ...styles.reportBarFill, width: `${pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Testimonial */}
        <div className="section-pad" style={styles.testimonialSection}>
          <div style={styles.testimonialQuote}>
            "Seeing the whole month helps me spot the patterns I can show my doctor —
            so I can advocate for the right treatment for my symptoms."
          </div>
          <div style={styles.testimonialMeta}>
            <div style={styles.testimonialDot} />
            <span style={styles.testimonialAuthor}>ApptNote beta user · tracking for 70 days</span>
          </div>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Pro section */}
        <div className="section-pad" style={styles.section}>
          <div style={styles.sectionLabel}>APPT NOTE PRO</div>
          <h2 style={styles.sectionHeading}>
            For women who want the full picture
          </h2>
          <p style={styles.sectionSub}>
            Pro unlocks everything you need to understand your body and advocate for yourself —
            at every chapter of the journey. From €59.99/year.
          </p>
          <div style={styles.proFeatures}>
            {[
              { title: 'Doctor-ready reports', body: 'Generated from your data, written in your voice. One before every appointment.' },
              { title: 'Before & after tracking', body: 'See how your symptoms change over time — whether you\'re waiting for answers or adjusting treatment.' },
              { title: 'Daily AI insights', body: 'Your trends pulled together, not just displayed. See what\'s shifting over time.' },
              { title: 'Unlimited history', body: 'Every chapter saved. Your full story, whenever you need it.' },
            ].map(({ title, body }) => (
              <div key={title} style={styles.proFeatureCard}>
                <div style={styles.proFeatureCheck}>✓</div>
                <div>
                  <div style={styles.proFeatureTitle}>{title}</div>
                  <div style={styles.proFeatureBody}>{body}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={styles.proCtas}>
            <button style={styles.ctaButton} onClick={onStartTracking}>
              Start free · Upgrade anytime
            </button>
            <p style={styles.proCtaNote}>€59.99/year · $69.99/year · Cancel anytime · Your data stays yours</p>
          </div>
        </div>

        {/* Trust / Security section */}
        <div className="section-pad" style={styles.section}>
          <div style={styles.sectionLabel}>YOUR DATA</div>
          <h2 style={styles.sectionHeading}>
            Built on trust.<br />
            <em style={{ fontStyle: 'italic', color: '#7A9E87' }}>Not on your data.</em>
          </h2>
          <p style={styles.sectionSub}>
            You're sharing some of the most personal information there is.
            Here's exactly how we protect it.
          </p>
          <div style={styles.trustGrid}>
            {[
              {
                title: 'Encrypted in transit and at rest',
                body: 'Your data is protected with AES-256 encryption at rest and TLS in transit — the same standard used by banks.',
              },
              {
                title: 'Your data is yours',
                body: 'Permanently delete everything — your logs, your insights, your history — at any time, in the app. Or email us for a full copy of your data.',
              },
              {
                title: 'We never sell your data',
                body: 'Your health information is never sold, rented, or shared with advertisers. Ever. It exists only to help you.',
              },
              {
                title: 'Explicit consent, always',
                body: 'We ask before we process. You decide what gets stored. You can withdraw consent and close your account any time.',
              },
            ].map(({ title, body }) => (
              <div key={title} style={styles.trustCard}>
                <div style={styles.trustCheck}>✓</div>
                <div>
                  <div style={styles.trustTitle}>{title}</div>
                  <div style={styles.trustBody}>{body}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={styles.trustFootnote}>
            GDPR compliant · Data stored in EU (Supabase) · Full privacy policy below
          </p>
        </div>

        <div style={styles.divider} />

        {/* Bottom CTA */}
        <div style={styles.bottomCta}>
          <div style={{ ...styles.sectionLabel, color: 'rgba(255,255,255,0.6)' }}>
            YOUR STORY STARTS HERE
          </div>
          <h2 style={styles.bottomCtaHeading}>
            This is your story.<br />
            <em style={{ fontStyle: 'italic' }}>Start writing it.</em>
          </h2>
          <p style={styles.bottomCtaSub}>
            Free to track. No credit card. No pressure. Just the tools to finally
            feel heard in every appointment.
          </p>
          <button style={styles.ctaButtonLight} onClick={onStartTracking}>
            Start your story free
          </button>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div style={styles.logo}>
            <span style={styles.logoAppt}>Appt</span>
            <span style={styles.logoNote}>Note</span>
          </div>
          <p style={styles.footerText}>
            ApptNote is not a medical service. AI insights are designed to help
            you communicate with your doctor, not replace medical advice.
            In an emergency call 112.
          </p>
          <div style={styles.footerLinks}>
            <button style={styles.footerLink} onClick={() => setShowPrivacy(true)}>
              Privacy Policy
            </button>
            <span style={styles.footerDot}>·</span>
            <button style={styles.footerLink} onClick={() => setShowTerms(true)}>
              Terms of Service
            </button>
            <span style={styles.footerDot}>·</span>
            <button style={styles.footerLink} onClick={onGetTheApp}>
              Get the App
            </button>
            <span style={styles.footerDot}>·</span>
            <a href="mailto:privacy@apptnote.com" style={styles.footerEmail}>privacy@apptnote.com</a>
          </div>
          {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
          {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
        </div>

      </div>
    </div>
  );
}

const serif = "'DM Serif Display', 'Georgia', serif";
const sans = "'DM Sans', system-ui, sans-serif";

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#FAF7F2',
    fontFamily: sans,
    overflowX: 'hidden',
    color: '#2C2C2A',
  },
  inner: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '0 32px',
  },

  // Header
  header: {
    padding: '28px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(44,44,42,0.08)',
    marginBottom: '0',
  },
  logo: { display: 'flex', alignItems: 'baseline', gap: '1px' },
  logoAppt: { fontSize: '24px', fontWeight: '700', color: '#2C2C2A', fontFamily: sans },
  logoNote: { fontSize: '24px', fontWeight: '700', color: '#D4A5A5', fontFamily: sans },
  navLinks: { display: 'flex', alignItems: 'center', gap: '24px' },
  navLink: {
    background: 'none', border: 'none', fontSize: '15px',
    fontWeight: '500', color: '#4a6070', cursor: 'pointer',
    fontFamily: sans, padding: '0',
  },
  navCta: {
    backgroundColor: '#7A9E87', color: 'white', border: 'none',
    borderRadius: '8px', padding: '10px 22px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer', fontFamily: sans,
  },

  // Hero
  hero: {
    textAlign: 'center',
    padding: '96px 0 80px',
  },
  heroPill: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#7A9E87',
    marginBottom: '28px',
    padding: '6px 16px',
    border: '1px solid rgba(122,158,135,0.3)',
    borderRadius: '100px',
  },
  headline: {
    fontFamily: serif,
    fontSize: '64px',
    fontWeight: '400',
    color: '#1a1a18',
    lineHeight: '1.08',
    marginBottom: '28px',
    margin: '0 auto 28px',
    letterSpacing: '-0.02em',
  },
  headlineEm: {
    fontStyle: 'italic',
    color: '#7A9E87',
  },
  subheadline: {
    fontSize: '19px',
    color: '#4a6070',
    lineHeight: '1.65',
    maxWidth: '560px',
    margin: '0 auto 48px',
    fontWeight: '300',
  },
  heroActions: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
  ctaButton: {
    backgroundColor: '#2C2C2A',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    padding: '18px 48px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: sans,
    letterSpacing: '0.01em',
  },
  ctaButtonOutline: {
    backgroundColor: 'transparent',
    color: '#2C2C2A',
    border: '1.5px solid rgba(44,44,42,0.25)',
    borderRadius: '10px',
    padding: '16px 36px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: sans,
    marginTop: '8px',
  },
  noSignup: { color: '#6c8396', fontSize: '13px', margin: '0', fontWeight: '400' },

  // Stats
  statsRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '80px',
    padding: '48px 0',
    borderTop: '1px solid rgba(44,44,42,0.08)',
    borderBottom: '1px solid rgba(44,44,42,0.08)',
  },
  statItem: { textAlign: 'center' },
  statNumber: {
    fontFamily: serif,
    fontSize: '44px',
    fontWeight: '400',
    color: '#1a1a18',
    lineHeight: '1',
    marginBottom: '8px',
  },
  statLabel: { fontSize: '13px', color: '#6c8396', fontWeight: '400', maxWidth: '120px' },

  // Divider
  divider: {
    height: '1px',
    backgroundColor: 'rgba(44,44,42,0.08)',
    margin: '0',
  },

  // Sections
  section: {
    padding: '96px 0',
  },
  sectionLabel: {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.14em',
    color: '#D4A5A5',
    textTransform: 'uppercase',
    marginBottom: '20px',
  },
  sectionHeading: {
    fontFamily: serif,
    fontSize: '44px',
    fontWeight: '400',
    color: '#1a1a18',
    lineHeight: '1.15',
    marginBottom: '16px',
    letterSpacing: '-0.015em',
  },
  sectionSub: {
    fontSize: '17px',
    color: '#4a6070',
    lineHeight: '1.65',
    maxWidth: '560px',
    marginBottom: '56px',
    fontWeight: '300',
  },

  // Chapters
  chaptersGrid: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
  },
  chapterCard: {
    flex: '1',
    minWidth: '200px',
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '32px 24px',
    border: '1px solid rgba(44,44,42,0.06)',
    boxShadow: '0 4px 20px rgba(44,44,42,0.04)',
  },
  chapterTag: {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#7A9E87',
    marginBottom: '16px',
  },
  chapterNum: {
    fontFamily: serif,
    fontSize: '48px',
    fontWeight: '400',
    color: '#D4A5A5',
    lineHeight: '1',
    marginBottom: '12px',
    opacity: '0.6',
  },
  chapterTitle: {
    fontFamily: serif,
    fontSize: '20px',
    fontWeight: '400',
    color: '#1a1a18',
    marginBottom: '12px',
    lineHeight: '1.25',
    fontStyle: 'italic',
  },
  chapterBody: {
    fontSize: '14px',
    color: '#4a6070',
    lineHeight: '1.7',
    margin: '0',
    fontWeight: '300',
  },

  // Report feature
  reportFeature: {
    display: 'flex',
    gap: '64px',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  reportText: { flex: '1', minWidth: '280px' },
  reportHeading: {
    fontFamily: serif,
    fontSize: '40px',
    fontWeight: '400',
    color: '#1a1a18',
    lineHeight: '1.15',
    marginBottom: '24px',
    letterSpacing: '-0.01em',
  },
  reportBody: {
    fontSize: '16px',
    color: '#4a6070',
    lineHeight: '1.7',
    marginBottom: '16px',
    fontWeight: '300',
  },
  reportVisual: { flex: '1', minWidth: '280px' },
  reportCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '28px',
    border: '1px solid rgba(44,44,42,0.08)',
    boxShadow: '0 8px 32px rgba(44,44,42,0.08)',
  },
  reportCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid rgba(44,44,42,0.06)',
  },
  reportCardLogo: { fontSize: '16px', fontWeight: '700', fontFamily: sans },
  reportCardDate: { fontSize: '11px', color: '#6c8396', fontWeight: '500' },
  reportCardSection: { marginBottom: '18px' },
  reportCardLabel: {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#7A9E87',
    marginBottom: '8px',
  },
  reportCardText: {
    fontSize: '13px',
    color: '#2C2C2A',
    lineHeight: '1.6',
    fontStyle: 'italic',
  },
  reportCardPoint: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '13px',
    color: '#2C2C2A',
    marginBottom: '6px',
  },
  reportCardDot: {
    width: '20px',
    height: '20px',
    backgroundColor: '#7A9E87',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: '700',
    flexShrink: '0',
  },
  reportCardGreeneBars: { marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(44,44,42,0.06)' },
  reportBarRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' },
  reportBarLabel: { fontSize: '11px', color: '#6c8396', width: '90px', flexShrink: 0 },
  reportBarTrack: { flex: 1, height: '6px', backgroundColor: '#F0EBE3', borderRadius: '3px', overflow: 'hidden' },
  reportBarFill: { height: '100%', backgroundColor: '#D4A5A5', borderRadius: '3px' },

  // Testimonial
  testimonialSection: {
    padding: '80px 0',
    textAlign: 'center',
  },
  testimonialQuote: {
    fontFamily: serif,
    fontSize: '28px',
    fontWeight: '400',
    color: '#1a1a18',
    lineHeight: '1.5',
    maxWidth: '680px',
    margin: '0 auto 28px',
    fontStyle: 'italic',
  },
  testimonialMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  testimonialDot: {
    width: '8px',
    height: '8px',
    backgroundColor: '#D4A5A5',
    borderRadius: '50%',
  },
  testimonialAuthor: { fontSize: '14px', color: '#6c8396', fontWeight: '400' },

  // Pro
  proFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxWidth: '600px',
    marginBottom: '40px',
  },
  proFeatureCard: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px 24px',
    border: '1px solid rgba(44,44,42,0.06)',
  },
  proFeatureCheck: {
    width: '22px',
    height: '22px',
    backgroundColor: '#7A9E87',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '700',
    flexShrink: 0,
    marginTop: '1px',
  },
  proFeatureTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#2C2C2A',
    marginBottom: '4px',
  },
  proFeatureBody: {
    fontSize: '14px',
    color: '#4a6070',
    lineHeight: '1.6',
    fontWeight: '300',
  },
  proCtas: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px' },
  proCtaNote: { fontSize: '13px', color: '#6c8396', margin: '0', fontWeight: '400' },

  // Trust section
  trustGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '16px',
    marginBottom: '28px',
  },
  trustCard: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px 24px',
    border: '1px solid rgba(44,44,42,0.06)',
  },
  trustCheck: {
    width: '22px',
    height: '22px',
    backgroundColor: '#7A9E87',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '700',
    flexShrink: 0,
    marginTop: '1px',
  },
  trustTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#2C2C2A',
    marginBottom: '4px',
  },
  trustBody: {
    fontSize: '14px',
    color: '#4a6070',
    lineHeight: '1.6',
    fontWeight: '300',
  },
  trustFootnote: {
    fontSize: '13px',
    color: '#6c8396',
    fontWeight: '400',
    margin: '0',
  },

  // Bottom CTA
  bottomCta: {
    backgroundColor: '#1a1a18',
    borderRadius: '24px',
    padding: '96px 64px',
    marginTop: '80px',
    marginBottom: '80px',
    textAlign: 'center',
  },
  bottomCtaHeading: {
    fontFamily: serif,
    fontSize: '52px',
    fontWeight: '400',
    color: 'white',
    lineHeight: '1.1',
    marginBottom: '20px',
    letterSpacing: '-0.02em',
  },
  bottomCtaSub: {
    fontSize: '17px',
    color: 'rgba(255,255,255,0.6)',
    maxWidth: '440px',
    margin: '0 auto 40px',
    lineHeight: '1.65',
    fontWeight: '300',
  },
  ctaButtonLight: {
    backgroundColor: 'white',
    color: '#1a1a18',
    border: 'none',
    borderRadius: '10px',
    padding: '18px 48px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: sans,
  },

  // Footer
  footer: {
    textAlign: 'center',
    padding: '40px 0 48px',
    borderTop: '1px solid rgba(44,44,42,0.08)',
  },
  footerText: {
    fontSize: '13px',
    color: '#6c8396',
    marginTop: '16px',
    maxWidth: '480px',
    margin: '16px auto 20px',
    lineHeight: '1.6',
    fontWeight: '300',
  },
  footerLinks: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  footerLink: {
    background: 'none', border: 'none', color: '#6c8396',
    fontSize: '13px', cursor: 'pointer', padding: '0',
    fontFamily: sans, textDecoration: 'underline',
  },
  footerDot: { color: 'rgba(44,44,42,0.2)', fontSize: '13px' },
  footerEmail: { color: '#6c8396', fontSize: '13px', textDecoration: 'none' },
};

export default LandingPage;
